import { Platform } from 'react-native';
import { storage } from '../utils/storage';
import { API_BASE_URL as BASE_URL } from './apiUrl';

/**
 * Technical telemetry for file uploads (practice videos, manifesto/agape
 * videos, feed-post video) — CORE-486. Separate from both the analytics
 * pipeline and RTC telemetry because:
 *   - Analytics events are *product* signals (page-views, clicks). Upload
 *     failures are *engineering* signal — connection drops mid-chunk, 413
 *     "file too large", presign 5xx, assemble timeouts — needed to answer
 *     "почему у мастера не грузится видео" without asking them to open
 *     DevTools. Keeping it out of AnalyticsEvents avoids polluting funnels.
 *   - It has its own typed dimensions (upload session, kind, transport,
 *     file size, bytes uploaded, error kind, http status) so problems can be
 *     queried per account / per upload session / per error from the admin
 *     panel and MCP/SQL.
 *
 * Mirrors src/services/liveTelemetry.ts (CORE-458/CORE-474) for its
 * reliability guarantees:
 *   - Queue survives reload/crash (localStorage, web only).
 *   - Idempotent: each event carries a client-generated id; the BE has a
 *     unique index on it and ignores duplicates, so re-sending a batch after
 *     a network blip — or after a resumed upload replays events — is safe.
 *   - Last-mile flush via navigator.sendBeacon on tab hide/close.
 * Native keeps an in-memory queue flushed on the same interval via fetch.
 */

const STORAGE_KEY = 'agape_upload_telemetry_queue_v1';
const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH_SIZE = 50;
const MAX_QUEUE_SIZE = 500;
const ENDPOINT = '/api/telemetry/upload-events';

/** How the bytes reached the backend. Note 'multipart' is the multipart/form-data
 *  POST (createPost); 'presigned-multipart' is the CORE-904 S3 multipart upload —
 *  distinct so the two don't merge in the upload analytics. */
export type UploadTransport = 'chunked' | 'presigned' | 'multipart' | 'presigned-multipart';

export interface UploadContext {
    /** Correlates every event of one upload attempt. For chunked uploads this
     *  is typically the BE sessionId; for presigned/multipart it's a
     *  client-generated id from newUploadSessionId(). */
    uploadSessionId: string;
    /** What is being uploaded: 'practice' | 'manifesto' | 'agape' | 'post' | 'cover' | 'avatar' | ... */
    kind: string;
    transport: UploadTransport;
    fileName?: string | null;
    fileSizeBytes?: number | null;
    contentType?: string | null;
}

interface PersistedEvent {
    /** Client-generated id; BE unique index dedupes on this. */
    clientEventId: string;
    eventName: string;
    uploadSessionId: string;
    kind: string;
    transport: UploadTransport;
    fileName: string | null;
    fileSizeBytes: number | null;
    contentType: string | null;
    bytesUploaded: number | null;
    durationMs: number | null;
    errorKind: string | null;
    httpStatus: number | null;
    pageUrl: string | null;
    timestampUtc: string;
    properties: Record<string, any>;
}

function isWeb(): boolean {
    return Platform.OS === 'web' && typeof window !== 'undefined';
}

/** Whether telemetry is captured at all. True on web (with a real window) and
 *  on native. Web-only reliability features keep the stricter isWeb() gate. */
function isEnabled(): boolean {
    return Platform.OS === 'web' ? typeof window !== 'undefined' : true;
}

function genId(): string {
    if (isWeb() && typeof crypto !== 'undefined' && (crypto as any).randomUUID) {
        return (crypto as any).randomUUID();
    }
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let s = '';
    for (let i = 0; i < 24; i++) s += chars[Math.floor(Math.random() * chars.length)];
    return `${Date.now().toString(36)}-${s}`;
}

function readPersistedQueue(): PersistedEvent[] {
    if (!isWeb()) return [];
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
}

function writePersistedQueue(queue: PersistedEvent[]): void {
    if (!isWeb()) return;
    try {
        // Drop OLDEST on overflow so the freshest failure info wins.
        const trimmed = queue.length > MAX_QUEUE_SIZE
            ? queue.slice(queue.length - MAX_QUEUE_SIZE)
            : queue;
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    } catch {
        // Quota exceeded etc. — never crash the app over telemetry.
    }
}

class UploadTelemetryClient {
    private queue: PersistedEvent[] = [];
    private flushTimer: ReturnType<typeof setInterval> | null = null;
    private isFlushing = false;

    constructor() {
        if (!isEnabled()) return;

        this.queue = readPersistedQueue();
        this.flushTimer = setInterval(() => { this.flush().catch(() => { }); }, FLUSH_INTERVAL_MS);

        if (isWeb()) {
            window.addEventListener('beforeunload', () => { this.flushBeacon(); });
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') this.flushBeacon();
            });
        }

        if (this.queue.length > 0) {
            setTimeout(() => { this.flush().catch(() => { }); }, 250);
        }
    }

    push(event: PersistedEvent) {
        if (!isEnabled()) return;
        this.queue.push(event);
        if (this.queue.length > MAX_QUEUE_SIZE) {
            this.queue = this.queue.slice(this.queue.length - MAX_QUEUE_SIZE);
        }
        writePersistedQueue(this.queue); // web-only; no-op on native
    }

    private async flush() {
        if (!isEnabled() || this.queue.length === 0 || this.isFlushing) return;
        this.isFlushing = true;
        const batch = this.queue.slice(0, MAX_BATCH_SIZE);
        try {
            const headers: HeadersInit = { 'Content-Type': 'application/json' };
            const token = await storage.getToken();
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const res = await fetch(`${BASE_URL}${ENDPOINT}`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ events: batch }),
            });

            if (res.ok || res.status === 204) {
                this.queue = this.queue.slice(batch.length);
                writePersistedQueue(this.queue);
            } else if (res.status === 400 || res.status === 413) {
                // Will never succeed on retry — drop so we don't loop forever.
                this.queue = this.queue.slice(batch.length);
                writePersistedQueue(this.queue);
            }
            // 5xx / network: keep the batch, retry next tick.
        } catch {
            // Network error — keep the batch queued.
        } finally {
            this.isFlushing = false;
        }
    }

    private flushBeacon() {
        if (!isWeb() || this.queue.length === 0) return;
        if (typeof navigator.sendBeacon !== 'function') return;
        try {
            const batch = this.queue.slice(0, MAX_BATCH_SIZE);
            const blob = new Blob([JSON.stringify({ events: batch })], { type: 'application/json' });
            const ok = navigator.sendBeacon(`${BASE_URL}${ENDPOINT}`, blob);
            if (ok) {
                this.queue = this.queue.slice(batch.length);
                writePersistedQueue(this.queue);
            }
        } catch { /* swallow */ }
    }
}

const client = new UploadTelemetryClient();

// ── Client-known user identity (mirrors liveTelemetry) ──────────────────
// The BE stamps the authoritative UserId from the request JWT, but last-mile
// beacon flushes cannot carry an Authorization header, so those batches would
// land unattributed. Stamp the client-known id into properties.userId too so
// every event is attributable regardless of transport. Kept in lock-step with
// auth via setUploadTelemetryUser (see AuthContext), mirroring setSentryUser /
// setTelemetryUser.
let currentUserId: string | null = null;

export function setUploadTelemetryUser(userId: string | null): void {
    currentUserId = userId;
}

function withTelemetryUser(properties: Record<string, any>): Record<string, any> {
    return currentUserId ? { ...properties, userId: currentUserId } : properties;
}

// ── Native device/OS context (mirrors liveTelemetry) ────────────────────
let cachedClientInfo: Record<string, any> | null = null;
function nativeClientInfo(): Record<string, any> {
    if (cachedClientInfo) return cachedClientInfo;
    const c: any = (Platform as any).constants ?? {};
    const info: Record<string, any> = {
        platform: Platform.OS,
        osVersion: `${Platform.Version}`,
    };
    if (c.systemName) info.systemName = c.systemName;
    if (c.Brand) info.deviceBrand = c.Brand;
    if (c.Model) info.deviceModel = c.Model;
    cachedClientInfo = info;
    return info;
}

function withClientInfo(properties: Record<string, any>): Record<string, any> {
    if (Platform.OS === 'web') return properties;
    return { ...properties, client: nativeClientInfo() };
}

function serializeError(err: unknown): Record<string, any> {
    if (err == null) return { message: 'unknown' };
    if (err instanceof Error) {
        return {
            message: err.message,
            name: err.name,
            code: (err as any).code,
            stack: err.stack?.slice(0, 2000),
        };
    }
    if (typeof err === 'object') {
        const obj = err as any;
        try {
            const { message, name, code, stack, ...rest } = obj;
            return {
                message: message ?? String(err),
                name,
                code,
                extra: Object.keys(rest).length > 0 ? rest : undefined,
                stack: typeof stack === 'string' ? stack.slice(0, 2000) : undefined,
            };
        } catch {
            return { message: String(err) };
        }
    }
    return { message: String(err) };
}

/** Pull an HTTP status off the common typed upload errors (ChunkUploadError /
 *  UploadError / AuthRequiredError all carry `.status`). */
function statusOf(err: unknown): number | null {
    const s = (err as any)?.status;
    return typeof s === 'number' ? s : null;
}

/** Best-effort error-kind label for grouping in the admin/MCP view. */
function errorKindOf(err: unknown): string {
    const name = (err as any)?.name;
    if (typeof name === 'string' && name) return name;
    if (err instanceof Error) return err.constructor?.name || 'Error';
    return 'unknown';
}

interface UploadEventFields {
    bytesUploaded?: number | null;
    durationMs?: number | null;
    errorKind?: string | null;
    httpStatus?: number | null;
}

function emit(
    eventName: string,
    ctx: UploadContext,
    fields: UploadEventFields,
    properties: Record<string, any>,
) {
    if (!isEnabled()) return;
    try {
        client.push({
            clientEventId: genId(),
            eventName,
            uploadSessionId: ctx.uploadSessionId,
            kind: ctx.kind,
            transport: ctx.transport,
            fileName: ctx.fileName ?? null,
            fileSizeBytes: ctx.fileSizeBytes ?? null,
            contentType: ctx.contentType ?? null,
            bytesUploaded: fields.bytesUploaded ?? null,
            durationMs: fields.durationMs ?? null,
            errorKind: fields.errorKind ?? null,
            httpStatus: fields.httpStatus ?? null,
            pageUrl: isWeb() ? window.location.pathname : null,
            timestampUtc: new Date().toISOString(),
            properties: withTelemetryUser(withClientInfo(properties)),
        });
    } catch { /* never let telemetry crash an upload */ }
}

// ── Public API ──────────────────────────────────────────────────────────

/** Correlation id for a presigned/multipart upload (chunked uses the BE
 *  sessionId as the correlation id instead). */
export function newUploadSessionId(): string {
    return `upl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Generic upload event with optional typed fields + free-form properties. */
export function trackUpload(
    eventName: string,
    ctx: UploadContext,
    props?: Record<string, any> & UploadEventFields,
) {
    const { bytesUploaded, durationMs, errorKind, httpStatus, ...rest } = props ?? {};
    emit(eventName, ctx, { bytesUploaded, durationMs, errorKind, httpStatus }, rest);
}

/** Error upload event — extracts http status + error kind from the thrown
 *  error and serializes it into properties.error. */
export function trackUploadError(
    eventName: string,
    ctx: UploadContext,
    err: unknown,
    props?: Record<string, any> & UploadEventFields,
) {
    const { bytesUploaded, durationMs, ...rest } = props ?? {};
    emit(
        eventName,
        ctx,
        {
            bytesUploaded,
            durationMs,
            errorKind: props?.errorKind ?? errorKindOf(err),
            httpStatus: props?.httpStatus ?? statusOf(err),
        },
        { ...rest, error: serializeError(err) },
    );
}
